import React, { useMemo } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Control,
  Controller,
  useFieldArray,
  UseFormReturn,
} from "react-hook-form";
import { DispatcherDayOfWeek, SimpleRule } from "../../types";
import { DispatcherRuleFormData } from "./DispatcherRuleForm";
import styles from "./RuleForm.module.css";

const DAY_LABELS: Record<DispatcherDayOfWeek, string> = {
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Gio",
  5: "Ven",
  6: "Sab",
  7: "Dom",
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const PCT_TOLERANCE = 0.001;

const formatHours = (s: { startHour: number; endHour: number }): string =>
  `${String(s.startHour).padStart(2, "0")}:00–${String(s.endHour).padStart(2, "0")}:59`;

type ScheduleValidity = {
  pctOk: boolean;
  hoursOk: boolean;
  overlapping: boolean;
  hasTargets: boolean;
  allTargetsHaveRule: boolean;
  totalPct: number;
};

function checkSchedule(
  s: DispatcherRuleFormData["schedules"][number],
  overlapMap: Set<number>,
  idx: number
): ScheduleValidity {
  const totalPct = s.targets.reduce(
    (acc, t) => acc + Number(t.percentage ?? 0),
    0
  );
  return {
    pctOk: Math.abs(totalPct - 100) <= PCT_TOLERANCE,
    hoursOk: s.startHour <= s.endHour,
    overlapping: overlapMap.has(idx),
    hasTargets: s.targets.length > 0,
    allTargetsHaveRule: s.targets.every((t) => !!t.simpleRuleId),
    totalPct,
  };
}

function isScheduleValid(v: ScheduleValidity): boolean {
  return (
    v.pctOk &&
    v.hoursOk &&
    !v.overlapping &&
    v.hasTargets &&
    v.allTargetsHaveRule
  );
}

interface Props {
  form: UseFormReturn<DispatcherRuleFormData>;
  simpleRules: SimpleRule[];
  activeDay: DispatcherDayOfWeek;
  setActiveDay: (day: DispatcherDayOfWeek) => void;
}

export default function ScheduleEditor({
  form,
  simpleRules,
  activeDay,
  setActiveDay,
}: Props) {
  const { control, watch, setValue } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "schedules",
  });

  const allSchedules = watch("schedules") ?? [];

  const indicesForDay = useMemo(() => {
    return fields
      .map((_, idx) => idx)
      .filter((idx) => allSchedules[idx]?.dayOfWeek === activeDay);
  }, [fields, allSchedules, activeDay]);

  const overlapMap = detectOverlaps(allSchedules);

  const dayStatusMap = (() => {
    const map = new Map<
      DispatcherDayOfWeek,
      { count: number; hasInvalid: boolean }
    >();
    allSchedules.forEach((s, idx) => {
      if (!s?.dayOfWeek) return;
      const validity = checkSchedule(s, overlapMap, idx);
      const cur = map.get(s.dayOfWeek) ?? { count: 0, hasInvalid: false };
      cur.count += 1;
      if (!isScheduleValid(validity)) cur.hasInvalid = true;
      map.set(s.dayOfWeek, cur);
    });
    return map;
  })();

  const handleAddSchedule = () => {
    append({
      dayOfWeek: activeDay,
      startHour: 9,
      endHour: 18,
      targets: [],
    });
  };

  const handleRemoveSchedule = (idx: number) => {
    remove(idx);
  };

  const handleHourChange = (
    scheduleIdx: number,
    which: "startHour" | "endHour",
    value: number
  ) => {
    setValue(`schedules.${scheduleIdx}.${which}`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <Box>
      <Tabs
        value={activeDay}
        onChange={(_, v) => setActiveDay(v as DispatcherDayOfWeek)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {(Object.keys(DAY_LABELS) as unknown as DispatcherDayOfWeek[]).map(
          (d) => {
            const day = Number(d) as DispatcherDayOfWeek;
            const status = dayStatusMap.get(day);
            return (
              <Tab
                key={day}
                value={day}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{DAY_LABELS[day]}</span>
                    {status && (
                      <Tooltip
                        title={
                          status.hasInvalid
                            ? "Uno o più schedule da completare"
                            : "Schedule validi"
                        }
                        arrow
                      >
                        <Box
                          component="span"
                          className={styles.scheduleTabBadge}
                          sx={{
                            backgroundColor: status.hasInvalid
                              ? "rgba(211, 47, 47, 0.15)"
                              : "rgba(46, 125, 50, 0.15)",
                            color: status.hasInvalid
                              ? "error.main"
                              : "success.main",
                            fontWeight: 600,
                          }}
                        >
                          {status.count}
                          {status.hasInvalid ? " !" : ""}
                        </Box>
                      </Tooltip>
                    )}
                  </Stack>
                }
              />
            );
          }
        )}
      </Tabs>

      {indicesForDay.length === 0 && (
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            p: 3,
            textAlign: "center",
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Nessuno schedule per {DAY_LABELS[activeDay]}.
          </Typography>
        </Box>
      )}

      {indicesForDay.map((scheduleIdx) => {
        const schedule = allSchedules[scheduleIdx];
        if (!schedule) return null;
        const validity = checkSchedule(schedule, overlapMap, scheduleIdx);
        return (
          <ScheduleCard
            key={fields[scheduleIdx]?.id ?? `schedule-${scheduleIdx}`}
            control={control}
            scheduleIdx={scheduleIdx}
            schedule={schedule}
            validity={validity}
            simpleRules={simpleRules}
            dayLabel={DAY_LABELS[activeDay]}
            onRemove={() => handleRemoveSchedule(scheduleIdx)}
            onHourChange={(which, value) =>
              handleHourChange(scheduleIdx, which, value)
            }
          />
        );
      })}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        size="small"
        onClick={handleAddSchedule}
      >
        Nuovo schedule per {DAY_LABELS[activeDay]}
      </Button>
    </Box>
  );
}

interface ScheduleCardProps {
  control: Control<DispatcherRuleFormData>;
  scheduleIdx: number;
  schedule: DispatcherRuleFormData["schedules"][number];
  validity: ScheduleValidity;
  simpleRules: SimpleRule[];
  dayLabel: string;
  onRemove: () => void;
  onHourChange: (which: "startHour" | "endHour", value: number) => void;
}

function ScheduleCard({
  control,
  scheduleIdx,
  schedule,
  validity,
  simpleRules,
  dayLabel,
  onRemove,
  onHourChange,
}: ScheduleCardProps) {
  const { pctOk, hoursOk, overlapping, totalPct } = validity;
  const invalid = !pctOk || !hoursOk || overlapping;

  const {
    fields: targetFields,
    append: appendTarget,
    remove: removeTarget,
  } = useFieldArray({
    control,
    name: `schedules.${scheduleIdx}.targets`,
  });

  return (
    <Box
      className={`${styles.scheduleCard} ${invalid ? styles.scheduleCardInvalid : ""}`}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600}>
          Schedule {dayLabel}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          size="small"
          color="error"
          onClick={onRemove}
          title="Rimuovi schedule"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Dalle</InputLabel>
          <Select
            label="Dalle"
            value={schedule.startHour}
            onChange={(e) =>
              onHourChange("startHour", Number(e.target.value))
            }
          >
            {HOURS.map((h) => (
              <MenuItem key={h} value={h}>
                {String(h).padStart(2, "0")}:00
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Alle</InputLabel>
          <Select
            label="Alle"
            value={schedule.endHour}
            onChange={(e) =>
              onHourChange("endHour", Number(e.target.value))
            }
          >
            {HOURS.map((h) => (
              <MenuItem key={h} value={h}>
                {String(h).padStart(2, "0")}:59
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!hoursOk && (
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" component="div">
                  Per coprire una fascia che attraversa la mezzanotte (es.
                  22:00 → 06:00), crea due schedule:
                </Typography>
                <Typography variant="caption" component="div">
                  • dalle 22 alle 23
                </Typography>
                <Typography variant="caption" component="div">
                  • dalle 00 alle 06
                </Typography>
              </Box>
            }
            arrow
          >
            <Alert severity="error" sx={{ py: 0, px: 1 }}>
              Ora fine &lt; ora inizio
            </Alert>
          </Tooltip>
        )}
        {overlapping && (
          <Alert severity="error" sx={{ py: 0, px: 1 }}>
            Fascia sovrapposta a un altro schedule
          </Alert>
        )}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Distribuzione (somma = 100%)
      </Typography>

      {targetFields.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", fontStyle: "italic", mb: 1 }}
        >
          Nessun target — aggiungi almeno una simple rule.
        </Typography>
      )}

      {targetFields.map((targetField, targetIdx) => (
        <Box key={targetField.id} className={styles.targetRow}>
          <Controller
            name={`schedules.${scheduleIdx}.targets.${targetIdx}.simpleRuleId`}
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                sx={{ flex: 2 }}
                size="small"
                options={simpleRules.filter((r) => r.active)}
                getOptionLabel={(opt) =>
                  typeof opt === "string"
                    ? opt
                    : `${opt.displayName} — ${opt.destination.url}`
                }
                value={simpleRules.find((r) => r.id === field.value) ?? null}
                onChange={(_, val) =>
                  field.onChange(
                    typeof val === "string" ? val : val?.id ?? ""
                  )
                }
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Simple rule"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
          <Controller
            name={`schedules.${scheduleIdx}.targets.${targetIdx}.percentage`}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="%"
                size="small"
                type="number"
                inputProps={{ min: 0, max: 100, step: 1 }}
                onChange={(e) => field.onChange(Number(e.target.value))}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ width: 100 }}
              />
            )}
          />
          <IconButton
            color="error"
            onClick={() => removeTarget(targetIdx)}
            title="Rimuovi target"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => appendTarget({ simpleRuleId: "", percentage: 0 })}
        >
          Aggiungi target
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <span
          className={`${styles.percentageBadge} ${pctOk ? styles.percentageOk : styles.percentageKo}`}
        >
          Somma: {totalPct}/100 {pctOk ? "✓" : "✗"}
        </span>
      </Stack>
    </Box>
  );
}

/**
 * Per ogni indice di schedule, ritorna true se interseca un altro schedule
 * dello stesso giorno. Intervallo inclusivo [startHour, endHour].
 */
function detectOverlaps(
  schedules: DispatcherRuleFormData["schedules"]
): Set<number> {
  const overlaps = new Set<number>();
  for (let i = 0; i < schedules.length; i++) {
    const a = schedules[i];
    if (!a || a.startHour > a.endHour) continue;
    for (let j = i + 1; j < schedules.length; j++) {
      const b = schedules[j];
      if (!b || b.startHour > b.endHour) continue;
      if (a.dayOfWeek !== b.dayOfWeek) continue;
      if (a.startHour <= b.endHour && b.startHour <= a.endHour) {
        overlaps.add(i);
        overlaps.add(j);
      }
    }
  }
  return overlaps;
}

export type SchedulesValidationResult =
  | { ok: true }
  | {
      ok: false;
      reason: string;
      offendingDay?: DispatcherDayOfWeek;
      offendingHours?: string;
      computedSum?: number;
    };

export function validateSchedulesPreSubmit(
  schedules: DispatcherRuleFormData["schedules"]
): SchedulesValidationResult {
  if (schedules.length === 0) {
    return { ok: false, reason: "Aggiungi almeno uno schedule" };
  }
  const overlapMap = detectOverlaps(schedules);
  for (let i = 0; i < schedules.length; i++) {
    const s = schedules[i];
    const dayLabel = DAY_LABELS[s.dayOfWeek];
    const hoursLabel = formatHours(s);
    const prefix = `Schedule ${dayLabel} ${hoursLabel}`;
    if (s.startHour > s.endHour) {
      return {
        ok: false,
        reason: `${prefix}: ora di fine precedente all'inizio`,
        offendingDay: s.dayOfWeek,
        offendingHours: hoursLabel,
      };
    }
    if (s.targets.length === 0) {
      return {
        ok: false,
        reason: `${prefix}: nessun target — aggiungi almeno una simple rule`,
        offendingDay: s.dayOfWeek,
        offendingHours: hoursLabel,
      };
    }
    const sum = s.targets.reduce(
      (acc, t) => acc + Number(t.percentage ?? 0),
      0
    );
    if (Math.abs(sum - 100) > PCT_TOLERANCE) {
      return {
        ok: false,
        reason: `${prefix}: somma percentuali ${sum}/100 (deve essere 100)`,
        offendingDay: s.dayOfWeek,
        offendingHours: hoursLabel,
        computedSum: sum,
      };
    }
    if (s.targets.some((t) => !t.simpleRuleId)) {
      return {
        ok: false,
        reason: `${prefix}: almeno un target senza simple rule selezionata`,
        offendingDay: s.dayOfWeek,
        offendingHours: hoursLabel,
      };
    }
  }
  if (overlapMap.size > 0) {
    const idx = schedules.findIndex((_, i) => overlapMap.has(i));
    const s = idx >= 0 ? schedules[idx] : undefined;
    if (s) {
      return {
        ok: false,
        reason: `Schedule ${DAY_LABELS[s.dayOfWeek]} ${formatHours(s)}: fascia sovrapposta a un altro schedule dello stesso giorno`,
        offendingDay: s.dayOfWeek,
        offendingHours: formatHours(s),
      };
    }
    return {
      ok: false,
      reason: "Ci sono fasce orarie sovrapposte nello stesso giorno",
    };
  }
  return { ok: true };
}
